import React from 'react';
import ReactDOMServer from 'react-dom/server';
import { Paper } from '@material-ui/core'; 
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  formattedCurrency,
  formatCPF,
} from '../../utils/utils';

// --- 1. Lógica de Preparação dos Dados ---
// Mantida idêntica à versão anterior
export const preparePromissoryData = ({
  client,
  companyData,
  totalValue,
  dueDate,
  emissionDate = new Date(),
  installments = null
}) => {
  if (!client) return null;

  const emissionObj = emissionDate ? new Date(emissionDate) : new Date();
  
  const city = companyData?.city || '________________';
  const creditor = companyData?.pseudonimo || 'Empresa';
  const clientName = `${client.name} ${client.lastName || ''}`.trim();
  const cpf = formatCPF(client.cpf) || '___________________';
  const clientAddress = `${client.street || ''}, ${client.houseNumber || ''} - ${client.neighborhood || ''}, ${client.city || ''}`;

  const createNoteData = (amount, dateObj, currentQuota, totalQuota) => {
    const docNumber = totalQuota > 1 
      ? `NP-${client.id}-${format(dateObj, 'yyyy')}-${currentQuota}/${totalQuota}`
      : `NP-${client.id}-${format(dateObj, 'yyyy')}`;

    const valueFormatted = formattedCurrency(amount);
    
    const dueDay = format(dateObj, 'dd');
    const dueMonth = format(dateObj, 'MMMM', { locale: ptBR });
    const dueYear = format(dateObj, 'yyyy');

    const legalText = `Ao dia ${dueDay} do mês de ${dueMonth} de ${dueYear}, pagarei(emos) por esta única via de Nota Promissória, na praça de ${city}, a quantia de ${valueFormatted} à ordem de ${creditor}, ou à sua ordem, em moeda corrente deste país, referente às vendas a crédito lançadas em aberto no sistema.`;

    return {
      docNumber,
      value: valueFormatted,
      emissionDateFull: format(emissionObj, 'dd/MM/yyyy HH:mm'),
      dueDateDisplay: format(dateObj, 'dd/MM/yyyy'),
      dueDay,
      dueMonth,
      dueYear,
      city,
      creditor,
      clientName,
      clientCpf: cpf,
      clientAddress,
      legalText,
      quotaInfo: totalQuota > 1 ? `${currentQuota}/${totalQuota}` : null
    };
  };

  if (installments && installments.length > 0) {
    return installments.map((inst, index) => 
      createNoteData(
        inst.value, 
        new Date(inst.dueDate), 
        index + 1, 
        installments.length
      )
    );
  }

  const dueObj = dueDate ? new Date(dueDate) : new Date();
  return createNoteData(totalValue, dueObj, 1, 1);
};

// --- 2. Componente Visual OTIMIZADO PARA 2 POR PÁGINA ---
const PromissoryNoteTemplate = ({ data, isPrint = false, index = 0 }) => {
  if (!data) return null;

  // Lógica: A primeira nota da folha é par (0), a segunda é ímpar (1).
  // Queremos quebrar a página SOMENTE após a ímpar (1, 3, 5...)
  const isThirdOnPage = (index + 1) % 3 === 0; 
  const isFirstOnPage = !isThirdOnPage;

  const styles = {
    container: isPrint ? {
      padding: '15px', // Padding reduzido para economizar espaço
      border: '2px solid #000',
      backgroundColor: '#fff',
      color: '#000',
      fontFamily: "'Roboto', Helvetica, Arial, sans-serif",
      maxWidth: '100%',
      boxSizing: 'border-box',
      position: 'relative',
      // Ajusta margem inferior se for a primeira da página para caber a próxima
      marginBottom: isFirstOnPage ? '40px' : '0', 
      // Quebra página apenas se for a segunda nota da folha
      pageBreakAfter: isThirdOnPage ? 'always' : 'auto'
    } : {
      padding: '30px',
      border: '1px solid #ccc',
      backgroundColor: '#fff',
      color: '#000',
      fontFamily: "'Roboto', Helvetica, Arial, sans-serif",
      marginBottom: '20px'
    },
    header: {
      textAlign: 'center',
      marginBottom: isPrint ? '15px' : '30px', // Margem reduzida
      textTransform: 'uppercase',
      fontSize: isPrint ? '20px' : '24px',
      fontWeight: 'bold',
      position: 'relative'
    },
    quotaBadge: {
        position: 'absolute',
        right: 0,
        top: 0,
        fontSize: '12px',
        border: '1px solid #000',
        padding: '2px 6px',
        borderRadius: '4px'
    },
    boxInfo: {
      border: '1px solid #ccc',
      padding: isPrint ? '10px' : '20px', // Padding reduzido
      borderRadius: '4px',
      backgroundColor: isPrint ? '#f0f0f0' : '#f9f9f9',
      marginBottom: isPrint ? '15px' : '30px',
      fontSize: '14px'
    },
    row: {
      display: 'flex',
      justifyContent: 'space-between',
      marginBottom: '5px',
      width: '100%'
    },
    colLeft: { width: '48%', textAlign: 'left' },
    colRight: { width: '48%', textAlign: 'right' },
    label: { fontWeight: 'bold', marginRight: '5px', fontSize: '13px' },
    value: { fontSize: '14px' },
    highlight: { color: '#d32f2f', fontWeight: 'bold', fontSize: isPrint ? '16px' : '18px' },
    legalText: { 
        marginBottom: isPrint ? '25px' : '60px', // Drasticamente reduzido no print para caber 2
        textAlign: 'justify', 
        lineHeight: isPrint ? '1.4' : '1.8', // Linhas mais compactas
        fontSize: isPrint ? '13px' : '16px' // Fonte ligeiramente menor
    },
    signaturesContainer: { 
        marginTop: isPrint ? '20px' : '50px', 
        display: 'flex', 
        justifyContent: 'space-between', 
        gap: '40px' 
    },
    signatureBlock: { flex: 1, textAlign: 'center' },
    signatureLine: { borderTop: '1px solid #000', paddingTop: '5px', width: '100%', margin: '0 auto' },
    roleTitle: { fontWeight: 'bold', textTransform: 'uppercase', fontSize: '11px', marginBottom: '5px' },
    footer: { textAlign: 'right', marginTop: '10px', fontSize: '9px', color: '#999' },
    
    // Estilo para a linha de corte visual entre as notas
    cutLine: {
      position: 'absolute',
      bottom: '-25px',
      left: 0,
      width: '100%',
      borderBottom: '1px dashed #999',
      height: '1px',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'flex-end'
    },
    cutText: {
      backgroundColor: '#fff',
      padding: '0 10px',
      fontSize: '10px',
      color: '#666',
      position: 'relative',
      top: '8px' // Ajuste fino para ficar sobre a linha
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        Nota Promissória
        {data.quotaInfo && <span style={styles.quotaBadge}>Parcela {data.quotaInfo}</span>}
      </div>

      <div style={styles.boxInfo}>
        <div style={styles.row}>
          <div style={styles.colLeft}>
            <span style={styles.label}>Valor:</span>
            <span style={styles.highlight}>{data.value}</span>
          </div>
          <div style={styles.colRight}>
            <span style={styles.label}>Data de Emissão:</span>
            <span style={styles.value}>{data.emissionDateFull}</span>
          </div>
        </div>
        <div style={styles.row}>
          <div style={styles.colLeft}>
            <span style={styles.label}>Vencimento:</span>
            <span style={styles.value}>{data.dueDateDisplay}</span>
          </div>
          <div style={styles.colRight}>
            <span style={styles.label}>Nº Documento:</span>
            <span style={styles.value}>{data.docNumber}</span>
          </div>
        </div>
      </div>

      <div style={styles.legalText}>
        {data.legalText}
      </div>

      <div style={styles.signaturesContainer}>
        <div style={styles.signatureBlock}>
          <div style={styles.signatureLine}>
            <div style={styles.roleTitle}>Emitente (Cliente)</div>
            <div style={{ fontSize: '12px' }}>{data.clientName}</div>
            <div style={{ fontSize: '11px', marginTop: '2px' }}>CPF: {data.clientCpf}</div>
          </div>
        </div>
        <div style={styles.signatureBlock}>
          <div style={styles.signatureLine}>
            <div style={styles.roleTitle}>Credor</div>
            <div style={{ fontSize: '12px' }}>{data.creditor}</div>
          </div>
        </div>
      </div>

      {isPrint && (
        <div style={styles.footer}>
          Documento gerado eletronicamente em {data.emissionDateFull}
        </div>
      )}

      {/* Exibe linha de corte apenas na impressão e se for a primeira nota da folha */}
      {isPrint && isFirstOnPage && (
        <div style={styles.cutLine}>
          <span style={styles.cutText}>✂ Corte Aqui</span>
        </div>
      )}
    </div>
  );
};

// --- 3. Função de Impressão (Passando Index) ---
export const printPromissoryNote = (noteDataInput) => {
  if (!noteDataInput) return;

  const dataArray = Array.isArray(noteDataInput) ? noteDataInput : [noteDataInput];

  // Passamos o index para o template controlar o page-break
  const componentsHtml = dataArray.map((data, index) => 
    ReactDOMServer.renderToStaticMarkup(
      <PromissoryNoteTemplate data={data} isPrint={true} index={index} />
    )
  ).join('');

  const iframe = document.createElement('iframe');
  Object.assign(iframe.style, {
    position: 'fixed', right: '0', bottom: '0', width: '0', height: '0', border: '0'
  });
  
  document.body.appendChild(iframe);

  const content = `
    <!DOCTYPE html>
    <html>
      <head>
        <title>Imprimir Notas</title>
        <style>
          @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;700&display=swap');
          body { 
            font-family: 'Roboto', Helvetica, Arial, sans-serif; 
            margin: 0; 
            padding: 10mm; /* Margem da folha A4 */
            background: white;
            box-sizing: border-box;
          }
          * { box-sizing: border-box; }
          /* Garante quebra de página na impressão correta */
          @media print {
            body { padding: 0; margin: 10mm; }
            div[style*="page-break-after: always"] {
                page-break-after: always !important;
            }
          }
        </style>
      </head>
      <body>
        ${componentsHtml}
      </body>
    </html>
  `;

  const doc = iframe.contentWindow.document;
  doc.open();
  doc.write(content);
  doc.close();

  iframe.onload = () => {
    setTimeout(() => {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
      setTimeout(() => {
        if (document.body.contains(iframe)) document.body.removeChild(iframe);
      }, 1000);
    }, 500);
  };
};

// --- 4. Componente Exportado para Pré-visualização ---
export const PromissoryNotePreview = ({ data }) => {
  if (!data) return null;
  const dataArray = Array.isArray(data) ? data : [data];

  return (
    <Paper elevation={0} style={{ padding: '0', background: 'transparent' }}>
      {dataArray.map((item, idx) => (
        <PromissoryNoteTemplate key={idx} data={item} isPrint={false} index={idx} />
      ))}
    </Paper>
  );
};